import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FlowLogo } from '../components/FlowLogo'
import SEO from '../components/SEO'

const Landing: React.FC = () => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  
  // If user is already logged in, redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  useEffect(() => {
    setIsVisible(true)
  }, [])

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* SEO Meta Tags */}
      <SEO
        title="MortgageFlow.pro - Professional Mortgage Broker CRM & Pipeline Management Software"
        description="Complete mortgage broker software solution with AI-powered automation, deal pipeline management, client CRM, task scheduling, and conversation tracking. Start your 14-day free trial today."
        keywords="mortgage broker software, mortgage CRM, loan origination system, mortgage pipeline management, mortgage broker tools, mortgage automation, mortgage lead management, broker CRM software, mortgage workflow automation, deal tracking software"
        url="https://mortgageflow.pro"
        type="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'MortgageFlow',
          applicationCategory: 'BusinessApplication',
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: '29',
            highPrice: '149',
            priceValidUntil: '2027-12-31',
            offers: [
              {
                '@type': 'Offer',
                name: 'Lite Plan',
                price: '29',
                priceCurrency: 'USD'
              },
              {
                '@type': 'Offer',
                name: 'Growth Plan',
                price: '59',
                priceCurrency: 'USD'
              },
              {
                '@type': 'Offer',
                name: 'Pro Plan',
                price: '99',
                priceCurrency: 'USD'
              }
            ]
          },
          operatingSystem: 'Web, iOS, Android',
          description: 'Complete mortgage broker software solution with AI-powered automation, deal pipeline management, client CRM, task scheduling, and conversation tracking.',
          url: 'https://mortgageflow.pro',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            ratingCount: '127'
          },
          featureList: [
            'Client CRM Management',
            'Deal Pipeline Tracking',
            'AI-Powered Automation',
            'Task Scheduling',
            'Email & SMS Integration',
            'Team Collaboration',
            'Custom Workflows',
            'Analytics & Reports'
          ]
        }}
      />
      
      {/* Navigation */}
      <nav className="relative z-50 px-6 py-5 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <FlowLogo size="md" animate={false} />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">MortgageFlow</h1>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <Link
              to="/login"
              className="px-5 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
            >
              {t('landing.nav.signIn')}
            </Link>
            <Link
              to="/login?register=true"
              className="px-6 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-all duration-200"
            >
              {t('landing.nav.getStarted')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative px-6 py-20 md:py-32 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gray-800/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gray-800/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className={`relative max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight tracking-tight">
              {t('landing.hero.title')}
              <span className="block bg-gradient-to-r from-gray-900 to-gray-900 bg-clip-text text-transparent">{t('landing.hero.titleHighlight')}</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('landing.hero.subtitle')}
            </p>
            
            <div className="flex flex-col gap-6 justify-center items-center mb-16">
              <Link
                to="/login?register=true"
                className="group px-8 py-4 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 hover:shadow-xl transition-all duration-200 text-lg flex items-center space-x-2"
              >
                <span>{t('landing.hero.startTrial')}</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              
              <p className="text-base text-gray-500 max-w-2xl leading-relaxed">
                {t('landing.hero.description')}
              </p>
            </div>

            {/* Visual mockup placeholder */}
            <div className="relative max-w-5xl mx-auto">
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-2xl p-1 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800/20 to-gray-800/20"></div>
                <div className="relative bg-white rounded-lg p-8">
                  <div className="space-y-4">
                    {/* Simulated dashboard UI */}
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-2">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <div className="h-8 w-64 bg-gray-100 rounded"></div>
                      <div className="w-8 h-8 bg-gray-100 rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 pt-6">
                      <div className="h-32 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 p-4 flex flex-col justify-between">
                        <div className="text-xs font-medium text-blue-700">{t('landing.mockup.widget1')}</div>
                        <div className="text-2xl font-bold text-blue-900">24</div>
                      </div>
                      <div className="h-32 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 p-4 flex flex-col justify-between">
                        <div className="text-xs font-medium text-purple-700">{t('landing.mockup.widget2')}</div>
                        <div className="text-2xl font-bold text-purple-900">12</div>
                      </div>
                      <div className="h-32 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 p-4 flex flex-col justify-between">
                        <div className="text-xs font-medium text-green-700">{t('landing.mockup.widget3')}</div>
                        <div className="text-2xl font-bold text-green-900">$8.4k</div>
                      </div>
                      <div className="h-32 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200 p-4 flex flex-col justify-between">
                        <div className="text-xs font-medium text-orange-700">{t('landing.mockup.widget4')}</div>
                        <div className="text-2xl font-bold text-orange-900">8</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="h-48 bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                      </div>
                      <div className="h-48 bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto pt-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 text-gray-900 rounded-lg mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">{t('landing.highlights.setupTime.label')}</div>
              <div className="text-2xl font-bold text-gray-900">{t('landing.highlights.setupTime.value')}</div>
              <p className="text-sm text-gray-600 mt-2">{t('landing.highlights.setupTime.description')}</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 text-green-600 rounded-lg mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">{t('landing.highlights.freeTrial.label')}</div>
              <div className="text-2xl font-bold text-gray-900">{t('landing.highlights.freeTrial.value')}</div>
              <p className="text-sm text-gray-600 mt-2">{t('landing.highlights.freeTrial.description')}</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 text-gray-900 rounded-lg mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">{t('landing.highlights.support.label')}</div>
              <div className="text-2xl font-bold text-gray-900">{t('landing.highlights.support.value')}</div>
              <p className="text-sm text-gray-600 mt-2">{t('landing.highlights.support.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Feature 1 */}
            <div className="group p-8 rounded-xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-gray-900 to-blue-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.features.clientManagement.title')}</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t('landing.features.clientManagement.description')}
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-gray-900 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.clientManagement.features.profiles')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-gray-900 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.clientManagement.features.followups')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-gray-900 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.clientManagement.features.documents')}
                </li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 rounded-xl bg-gradient-to-br from-purple-50 to-white border border-purple-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-gray-900 to-purple-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.features.dealPipeline.title')}</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t('landing.features.dealPipeline.description')}
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-gray-900 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.dealPipeline.features.kanban')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-gray-900 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.dealPipeline.features.commission')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-gray-900 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.dealPipeline.features.progress')}
                </li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className="group p-8 rounded-xl bg-gradient-to-br from-green-50 to-white border border-green-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.features.aiAutomation.title')}</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t('landing.features.aiAutomation.description')}
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.aiAutomation.features.summaries')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.aiAutomation.features.scheduling')}
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('landing.features.aiAutomation.features.tasks')}
                </li>
              </ul>
            </div>
          </div>

          {/* Additional Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.calendar.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.calendar.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.teamCollaboration.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.teamCollaboration.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.leadManagement.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.leadManagement.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.emailIntegration.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.emailIntegration.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.workflows.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.workflows.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.customForms.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.customForms.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.reports.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.reports.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.analytics.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.analytics.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.customDashboard.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.customDashboard.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.communications.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.communications.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.security.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.security.description')}</p>
            </div>
            <div className="p-6 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{t('landing.features.mobile.title')}</h4>
              <p className="text-gray-600 text-sm">{t('landing.features.mobile.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="px-6 py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              {t('landing.pricing.subtitle')}
            </p>
            
            {/* Billing Toggle */}
            <div className="flex flex-col items-center gap-3 mb-8">
              <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setIsVisible(true)}
                  className={`px-6 py-2 rounded-md font-medium transition-all ${isVisible ? 'bg-white text-gray-900 shadow' : 'text-gray-600'}`}
                >
                  {t('landing.pricing.monthly')}
                </button>
                <button
                  onClick={() => setIsVisible(false)}
                  className={`px-6 py-2 rounded-md font-medium transition-all ${!isVisible ? 'bg-white text-gray-900 shadow' : 'text-gray-600'}`}
                >
                  {t('landing.pricing.annually')}
                  <span className="ml-2 text-xs text-green-600 font-semibold">{t('landing.pricing.annuallySave')}</span>
                </button>
              </div>
              
              <div className="inline-flex items-center bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 font-medium">{t('landing.pricing.freeTrial')}</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {/* Lite Plan */}
            <div className="rounded-xl border-2 border-gray-200 p-8 bg-white hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.pricing.lite.name')}</h3>
              <p className="text-gray-600 text-sm mb-6">{t('landing.pricing.lite.description')}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">
                  ${isVisible ? t('landing.pricing.lite.monthlyPrice') : t('landing.pricing.lite.annualPrice')}
                </span>
                <span className="text-gray-600">{t('landing.pricing.perMonth')}</span>
                {!isVisible && <p className="text-sm text-gray-500 mt-1">{t('landing.pricing.billed')} annually</p>}
              </div>
              <Link
                to="/login?register=true"
                className="block w-full text-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all mb-6"
              >
                {t('landing.pricing.lite.cta')}
              </Link>
              <ul className="space-y-3">
                {['Up to 10 active deals', 'Up to 20 active leads', 'Basic CRM features', 'Email & SMS integration', 'Mobile app access', 'Email support'].map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-600">
                    <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Growth Plan */}
            <div className="rounded-xl border-2 border-gray-900 p-8 bg-gray-900 text-white hover:shadow-2xl transition-all duration-300 relative">
              <div className="absolute top-0 right-8 -translate-y-1/2">
                <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {t('landing.pricing.growth.popular')}
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-2">{t('landing.pricing.growth.name')}</h3>
              <p className="text-gray-300 text-sm mb-6">{t('landing.pricing.growth.description')}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">
                  ${isVisible ? t('landing.pricing.growth.monthlyPrice') : t('landing.pricing.growth.annualPrice')}
                </span>
                <span className="text-gray-300">{t('landing.pricing.perMonth')}</span>
                {!isVisible && <p className="text-sm text-gray-400 mt-1">{t('landing.pricing.billed')} annually</p>}
              </div>
              <Link
                to="/login?register=true"
                className="block w-full text-center px-6 py-3 bg-white text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-all mb-6"
              >
                {t('landing.pricing.growth.cta')}
              </Link>
              <ul className="space-y-3">
                {['Unlimited deals & leads', 'Advanced CRM & automation', 'Team collaboration', 'Custom workflows', 'Contact forms', 'Priority support'].map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-300">
                    <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro Plan */}
            <div className="rounded-xl border-2 border-gray-200 p-8 bg-white hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.pricing.pro.name')}</h3>
              <p className="text-gray-600 text-sm mb-6">{t('landing.pricing.pro.description')}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">
                  ${isVisible ? t('landing.pricing.pro.monthlyPrice') : t('landing.pricing.pro.annualPrice')}
                </span>
                <span className="text-gray-600">{t('landing.pricing.perMonth')}</span>
                {!isVisible && <p className="text-sm text-gray-500 mt-1">{t('landing.pricing.billed')} annually</p>}
              </div>
              <Link
                to="/login?register=true"
                className="block w-full text-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all mb-6"
              >
                {t('landing.pricing.pro.cta')}
              </Link>
              <ul className="space-y-3">
                {['Everything in Growth', 'Calendar & Appointments', 'Calendly integration', 'Custom forms', 'AI Workflows management', 'Advanced analytics & reports', 'White-label options', 'Dedicated account manager'].map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-600">
                    <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Enterprise Plan */}
            <div className="rounded-xl border-2 border-gray-200 p-8 bg-white hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.pricing.enterprise.name')}</h3>
              <p className="text-gray-600 text-sm mb-6">{t('landing.pricing.enterprise.description')}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">{t('landing.pricing.enterprise.price')}</span>
              </div>
              <a
                href="/enterprise-contact"
                className="block w-full text-center px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-all mb-6"
              >
                {t('landing.pricing.enterprise.cta')}
              </a>
              <ul className="space-y-3">
                {['Everything in Pro', 'Custom development', 'SLA guarantees', 'Advanced security', 'Personalized onboarding', 'API access'].map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-600">
                    <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
            {t('landing.cta.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Link
              to="/login?register=true"
              className="group px-8 py-4 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 hover:shadow-xl transition-all duration-200 text-lg flex items-center space-x-2"
            >
              <span>{t('landing.cta.getStarted')}</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <Link
              to="/login"
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 text-lg"
            >
              {t('landing.cta.signIn')}
            </Link>
          </div>
          <p className="text-gray-500 text-sm">
            {t('landing.cta.footer')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-6 md:mb-0">
              <FlowLogo size="sm" animate={false} />
              <span className="text-gray-900 font-medium">MortgageFlow</span>
            </div>
            
            <div className="flex space-x-8 text-gray-600 text-sm">
              <Link to="/privacy" className="hover:text-gray-900 transition-colors">{t('landing.footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-gray-900 transition-colors">{t('landing.footer.terms')}</Link>
              <Link to="/contact" className="hover:text-gray-900 transition-colors">{t('landing.footer.contact')}</Link>
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-gray-600 text-sm">
              {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Landing 